import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ExcelEditor from "@/components/ExcelEditor";
import {
  FilePdf, FileDoc, Globe, UploadSimple, Plus, Trash, MagnifyingGlass, CircleNotch, CheckCircle, XCircle, CalendarBlank,
  Table, PencilSimple,
} from "@phosphor-icons/react";

const TYPE = {
  pdf: { icon: FilePdf, color: "#dc2626", bg: "#fee2e2", label: "PDF" },
  word: { icon: FileDoc, color: "#2563eb", bg: "#dbeafe", label: "Word" },
  url: { icon: Globe, color: "#7c3aed", bg: "#f3e8ff", label: "URL" },
  excel: { icon: Table, color: "#15803d", bg: "#e7f6ec", label: "Excel" },
};

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(m) {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
}
const MONTHS = [
  { v: "01", n: "January" }, { v: "02", n: "February" }, { v: "03", n: "March" },
  { v: "04", n: "April" }, { v: "05", n: "May" }, { v: "06", n: "June" },
  { v: "07", n: "July" }, { v: "08", n: "August" }, { v: "09", n: "September" },
  { v: "10", n: "October" }, { v: "11", n: "November" }, { v: "12", n: "December" },
];
// SECURITY FIX (Insecure URL Source Validation): client-side checks for fast
// feedback. They MUST mirror backend url_security.py, which is the real
// control (the API can be called directly, bypassing this page).
const ALLOWED_URL_DOMAINS = [
  "gov.in", "nic.in", "rbi.org.in", "pib.gov.in", "mospi.gov.in", "ppac.gov.in",
  "petroleum.nic.in", "sebi.gov.in", "gailonline.com",
];
const MAX_URL_LENGTH = 2048;
const MAX_NAME_LENGTH = 150;
const FORBIDDEN_NAME_CHARS = /[<>{}`$\\\u0000-\u001f\u007f]/;

function validateSourceUrl(raw) {
  const value = (raw || "").trim();
  if (!value) return "Enter a URL";
  if (value.length > MAX_URL_LENGTH) return "URL is too long";
  if (/[\s\\]/.test(value)) return "URL contains invalid characters";
  let u;
  try { u = new URL(value); } catch { return "Enter a valid URL"; }
  if (u.protocol !== "https:") return "Only https:// links are allowed";
  if (u.username || u.password) return "Links with login details are not allowed";
  if (u.port && u.port !== "443") return "Non-standard ports are not allowed";
  const host = u.hostname.toLowerCase().replace(/\.$/, "");
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith("[")) return "IP addresses are not allowed; use a domain name";
  if (!ALLOWED_URL_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`))) {
    return "This domain is not an approved source";
  }
  return null;
}

function validateSourceName(raw) {
  const value = (raw || "").trim();
  if (value.length > MAX_NAME_LENGTH) return `Name must be at most ${MAX_NAME_LENGTH} characters`;
  if (FORBIDDEN_NAME_CHARS.test(value)) return "Name cannot contain < > { } ` $ \\";
  if (/^[=+\-@]/.test(value)) return "Name cannot start with = + - @";
  return null;
}

function defaultNameFromUrl(value) {
  try {
    const u = new URL(value);
    return `${u.hostname}${u.pathname === "/" ? "" : u.pathname}`.slice(0, MAX_NAME_LENGTH);
  } catch { return value.slice(0, MAX_NAME_LENGTH); }
}

const YEARS = (() => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2, y - 3].map(String);
})();

export default function Repository() {
  const [docs, setDocs] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [url, setUrl] = useState("");
  const [urlName, setUrlName] = useState("");
  const [adding, setAdding] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [nameError, setNameError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scrapeResult, setScrapeResult] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [editorData, setEditorData] = useState(null);
  const fileRef = useRef();

  const load = (m = month) => api.get(`/documents?month=${m}`).then((r) => setDocs(r.data));
  useEffect(() => { load(month); }, [month]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const lower = file.name.toLowerCase();
    if (lower.endsWith(".xls") || lower.endsWith(".xlsx")) {
      setUploading(true);
      const fd = new FormData(); fd.append("file", file);
      try {
        const { data } = await api.post("/documents/parse-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const sheet = data.sheets?.[0] || { columns: [], rows: [] };
        setEditorData({ name: file.name.replace(/\.(xls|xlsx)$/i, ""), month, columns: sheet.columns, rows: sheet.rows });
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read Excel"); }
      finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
      return;
    }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("month", month);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded & text extracted"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/documents/${id}`);
      setEditorData({
        id: data.id, name: data.name, month: data.month,
        columns: data.sheet_data?.columns || [], rows: data.sheet_data?.rows || [],
        pib_category: data.pib_category || "", chart_label_col: data.chart_label_col, chart_value_col: data.chart_value_col,
      });
    } catch { toast.error("Could not open spreadsheet"); }
  };

  const saveExcel = async (payload) => {
    try {
      if (payload.id) await api.put(`/documents/${payload.id}`, payload);
      else await api.post("/documents/excel", payload);
      toast.success("Spreadsheet saved to repository");
      setEditorData(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
  };

  const addUrl = async () => {
    const cleanUrl = url.trim();
    const uErr = validateSourceUrl(cleanUrl);
    const nErr = validateSourceName(urlName);
    setUrlError(uErr || ""); setNameError(nErr || "");
    if (uErr || nErr) return;
    setAdding(true);
    try {
      const { data } = await api.post("/documents/url", {
        url: cleanUrl,
        name: urlName.trim() || defaultNameFromUrl(cleanUrl),
        month,
      });
      toast[data.scrape_success ? "success" : "warning"](data.scrape_success ? "URL source added & scraped" : "URL added (scrape returned no text)");
      setUrl(""); setUrlName(""); load();
    } catch (err) {
      // Backend rejection (400) is shown inline, same as client-side errors.
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Could not add URL source";
      if (err.response?.status === 400 || err.response?.status === 422) setUrlError(msg);
      else toast.error(msg);
    }
    finally { setAdding(false); }
  };

  const remove = async (id) => { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); };

  const runScrape = async () => {
    const err = validateSourceUrl(scrapeUrl);
    if (err) { toast.error(err); return; }
    setScraping(true); setScrapeResult(null);
    try { const { data } = await api.post("/documents/scrape", { url: scrapeUrl.trim() }); setScrapeResult(data); }
    catch (err) { toast.error("Scrape failed"); }
    finally { setScraping(false); }
  };

  return (
    <div className="space-y-8" data-testid="repository-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-[#b45309] bg-[#fef3e2] rounded-full px-3 py-1 inline-block">Sources</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mt-3">Document Repository</h1>
          <p className="text-slate-500 text-base mt-1">PDF, Word and URL sources — preserved month-wise. Uploads apply to the selected month.</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 h-12 shadow-sm">
          <CalendarBlank size={18} className="text-[#004182]" />
          <select data-testid="year-select" value={month.split("-")[0]}
            onChange={(e) => setMonth(`${e.target.value}-${month.split("-")[1]}`)}
            className="bg-transparent text-slate-900 text-sm font-medium outline-none cursor-pointer">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <span className="text-slate-300">/</span>
          <select data-testid="month-select" value={month.split("-")[1]}
            onChange={(e) => setMonth(`${month.split("-")[0]}-${e.target.value}`)}
            className="bg-transparent text-slate-900 text-sm font-medium outline-none cursor-pointer">
            {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.n}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card p-5">
          <div className="flex items-center gap-2 text-slate-900 font-semibold"><UploadSimple size={18} className="text-[#F59E0B]" /> Upload File</div>
          <p className="text-xs text-slate-500 mt-1">PDF, Word (.docx) or Excel (.xls/.xlsx). Excel opens an editor to trim &amp; edit before saving.</p>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.xls,.xlsx" className="hidden" onChange={onUpload} data-testid="file-input" />
          <Button data-testid="upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="gold-btn rounded-xl mt-3 w-full h-11 gap-2">
            {uploading ? <><CircleNotch size={16} className="spin-slow" /> Uploading…</> : <><Plus size={16} weight="bold" /> Choose File</>}
          </Button>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-2 text-slate-900 font-semibold"><Globe size={18} className="text-purple-600" /> Add URL Source</div>
          <div className="space-y-2 mt-3">
            <Input data-testid="url-name-input" value={urlName}
              onChange={(e) => { setUrlName(e.target.value); if (nameError) setNameError(""); }}
              placeholder="Name (optional)" maxLength={MAX_NAME_LENGTH} autoComplete="off"
              aria-invalid={!!nameError} className={`h-10 ${nameError ? "border-red-500" : ""}`} />
            {nameError && <p className="text-xs text-red-600" data-testid="url-name-error">{nameError}</p>}
            <div className="flex gap-2">
              <Input data-testid="url-input" type="url" inputMode="url" value={url}
                onChange={(e) => { setUrl(e.target.value); if (urlError) setUrlError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") addUrl(); }}
                placeholder="https://…" maxLength={MAX_URL_LENGTH} autoComplete="off" spellCheck={false}
                aria-invalid={!!urlError} className={`h-10 ${urlError ? "border-red-500" : ""}`} />
              <Button data-testid="add-url-source-btn" onClick={addUrl} disabled={adding || !url.trim()} className="gold-btn rounded-xl h-10 px-3">
                {adding ? <CircleNotch size={16} className="spin-slow" /> : <Plus size={16} weight="bold" />}
              </Button>
            </div>
            {urlError
              ? <p className="text-xs text-red-600" data-testid="url-error">{urlError}</p>
              : <p className="text-xs text-slate-500">Only https:// links from approved sources (e.g. pib.gov.in, rbi.org.in, mospi.gov.in).</p>}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr className="text-left">
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Type</th>
              <th className="px-5 py-3 font-medium hidden sm:table-cell">Month</th>
              <th className="px-5 py-3 font-medium hidden md:table-cell">Source</th>
              <th className="px-5 py-3 font-medium hidden md:table-cell">Extracted</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => {
              const t = TYPE[d.doc_type] || TYPE.url;
              return (
                <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-5 py-3 text-slate-900 font-medium">{d.name}</td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium" style={{ color: t.color, background: t.bg }}>
                      <t.icon size={13} weight="fill" /> {t.label}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-500 hidden sm:table-cell">{monthLabel(d.month)}</td>
                  <td className="px-5 py-3 text-slate-500 hidden md:table-cell max-w-[220px] truncate">{d.url || "—"}</td>
                  <td className="px-5 py-3 text-slate-400 hidden md:table-cell">{d.extracted_text ? `${d.extracted_text.length} chars` : "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      {d.doc_type === "excel" && (
                        <button data-testid={`edit-doc-${d.id}`} onClick={() => openEdit(d.id)} className="text-slate-400 hover:text-[#004182]" title="Edit spreadsheet"><PencilSimple size={16} /></button>
                      )}
                      <button data-testid={`delete-doc-${d.id}`} onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-500"><Trash size={16} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {docs.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400" data-testid="no-docs">No documents for {monthLabel(month)}. Upload PDFs, Excel or add URLs for this month.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editorData && (
        <ExcelEditor initial={editorData} onSave={saveExcel} onClose={() => setEditorData(null)} />
      )}
    </div>
  );
}